import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface OrderRow {
  id: string;
  total_amount: number;
  status: string;
  created_at: string;
  items: Array<{ name: string; quantity: number; points: number }>;
}

interface PointsLogRow {
  id: string;
  delta: number;
  type: 'income' | 'expense';
  source: string;
  created_at: string;
}

interface DayStat {
  date: string;
  orderCount: number;
  points: number;
  pointsIn: number;
  pointsOut: number;
}

interface ProductStat {
  name: string;
  quantity: number;
  points: number;
}

const STATUS_COLORS: Record<string, string> = {
  '待发货': '#fbbf24',
  '待收货': '#38bdf8',
  '已完成': '#34d399',
  '售后中': '#fb7185',
  '已退款': '#9ca3af',
};

const SOURCE_COLORS: Record<string, string> = {
  manual: '#f43f5e',
  order: '#10b981',
  recharge: '#3b82f6',
  transfer_in: '#8b5cf6',
  transfer_out: '#f59e0b',
  transfer: '#a78bfa',
  reward: '#ec4899',
  refund: '#06b6d4',
};

const SOURCE_LABELS: Record<string, string> = {
  manual: '后台调整',
  order: '订单消费',
  recharge: '充值获得',
  transfer_in: '积分转入',
  transfer_out: '积分转出',
  transfer: '积分转账',
  reward: '活动奖励',
  refund: '退款返还',
};

const RANGE_OPTIONS = [
  { label: '近7天', days: 7 },
  { label: '近30天', days: 30 },
  { label: '近90天', days: 90 },
];

const AdminStatsPage = () => {
  const [range, setRange] = useState(7);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [pointsLogs, setPointsLogs] = useState<PointsLogRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - range);

    const [ordersRes, logsRes] = await Promise.all([
      supabase
        .from('orders')
        .select('id, total_amount, status, created_at, items')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: true }),
      supabase
        .from('points_log')
        .select('id, delta, type, source, created_at')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: true }),
    ]);

    if (!ordersRes.error && ordersRes.data) setOrders(ordersRes.data as OrderRow[]);
    if (!logsRes.error && logsRes.data) setPointsLogs(logsRes.data as PointsLogRow[]);
    setLoading(false);
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── 核心指标 ──────────────────────────────────────────────
  const validOrders = orders.filter((o) => o.status !== '已退款');
  const totalPointsConsumed = validOrders.reduce((s, o) => s + Number(o.total_amount), 0);
  const totalOrders = orders.length;
  const completedOrders = orders.filter((o) => o.status === '已完成').length;
  const refundOrders = orders.filter((o) => o.status === '已退款').length;

  const pointsIn = pointsLogs.filter((l) => l.type === 'income' && l.source !== 'refund').reduce((s, l) => s + l.delta, 0);
  const pointsOut = pointsLogs.filter((l) => l.type === 'expense').reduce((s, l) => s + Math.abs(l.delta), 0);
  const avgOrderPoints = validOrders.length > 0 ? totalPointsConsumed / validOrders.length : 0;

  // ── 每日趋势 ──────────────────────────────────────────────
  const dayStats: DayStat[] = (() => {
    const map: Record<string, DayStat> = {};
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
      map[key] = { date: key, orderCount: 0, points: 0, pointsIn: 0, pointsOut: 0 };
    }
    orders.forEach((o) => {
      const key = new Date(o.created_at).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
      if (map[key]) {
        map[key].orderCount += 1;
        if (o.status !== '已退款') {
          map[key].points += Number(o.total_amount);
        }
      }
    });
    pointsLogs.forEach((l) => {
      const key = new Date(l.created_at).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
      if (map[key]) {
        if (l.type === 'income' && l.source !== 'refund') {
          map[key].pointsIn += l.delta;
        } else if (l.type === 'expense') {
          map[key].pointsOut += Math.abs(l.delta);
        }
      }
    });
    return Object.values(map);
  })();

  const maxPoints = Math.max(...dayStats.map((d) => d.points), 1);
  const maxPointsIn = Math.max(...dayStats.map((d) => d.pointsIn), 1);
  const maxCount = Math.max(...dayStats.map((d) => d.orderCount), 1);

  // ── 订单状态分布 ──────────────────────────────────────────
  const statusDist: Record<string, number> = {};
  orders.forEach((o) => {
    statusDist[o.status] = (statusDist[o.status] ?? 0) + 1;
  });
  const statusEntries = Object.entries(statusDist).sort((a, b) => b[1] - a[1]);

  // ── 积分来源分布 ──────────────────────────────────────────
  const sourceDist: Record<string, number> = {};
  pointsLogs.forEach((l) => {
    sourceDist[l.source] = (sourceDist[l.source] ?? 0) + Math.abs(l.delta);
  });
  const sourceEntries = Object.entries(sourceDist)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const totalSourcePoints = sourceEntries.reduce((s, [, v]) => s + v, 0);

  // ── 商品销售排行 ──────────────────────────────────────────
  const productMap: Record<string, ProductStat> = {};
  validOrders.forEach((o) => {
    if (!Array.isArray(o.items)) return;
    o.items.forEach((item) => {
      const name = item.name ?? '未知商品';
      if (!productMap[name]) productMap[name] = { name, quantity: 0, points: 0 };
      productMap[name].quantity += item.quantity ?? 1;
      productMap[name].points += (item.points ?? 0) * (item.quantity ?? 1);
    });
  });
  const topProducts = Object.values(productMap)
    .sort((a, b) => b.points - a.points)
    .slice(0, 8);
  const maxProductPoints = Math.max(...topProducts.map((p) => p.points), 1);

  // ── 小时分布 ──────────────────────────────────────────────
  const hourDist = Array(24).fill(0);
  orders.forEach((o) => {
    const h = new Date(o.created_at).getHours();
    hourDist[h] += 1;
  });
  const maxHour = Math.max(...hourDist, 1);
  const peakHour = hourDist.indexOf(Math.max(...hourDist));

  return (
    <AdminLayout title="数据统计">
      {/* Range selector */}
      <div className="flex items-center gap-2 mb-6">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            onClick={() => setRange(opt.days)}
            className={`px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all ${
              range === opt.days
                ? 'bg-rose-500 text-white font-medium'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-rose-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">数据实时更新</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* ── 核心指标卡片 ── */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            {[
              {
                label: '总订单数',
                value: totalOrders.toLocaleString(),
                sub: `已完成 ${completedOrders} 笔`,
                icon: 'ri-file-list-3-line',
                color: 'text-rose-500 bg-rose-50',
              },
              {
                label: '积分消耗',
                value: totalPointsConsumed.toLocaleString(),
                sub: '兑换商品消耗',
                icon: 'ri-shopping-bag-line',
                color: 'text-emerald-500 bg-emerald-50',
              },
              {
                label: '积分发放',
                value: pointsIn.toLocaleString(),
                sub: '含退款、奖励等',
                icon: 'ri-add-circle-line',
                color: 'text-sky-500 bg-sky-50',
              },
              {
                label: '退款订单',
                value: refundOrders.toLocaleString(),
                sub: `占比 ${totalOrders > 0 ? ((refundOrders / totalOrders) * 100).toFixed(1) : 0}%`,
                icon: 'ri-refund-2-line',
                color: 'text-amber-500 bg-amber-50',
              },
              {
                label: '平均订单积分',
                value: Math.round(avgOrderPoints).toLocaleString(),
                sub: '有效订单均值',
                icon: 'ri-copper-coin-line',
                color: 'text-violet-500 bg-violet-50',
              },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-xl p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${card.color}`}>
                  <i className={`${card.icon} text-2xl`} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">{card.label}</p>
                  <p className="text-xl font-bold text-gray-800">{card.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── 积分趋势 + 订单状态 ── */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {/* 积分趋势柱状图 */}
            <div className="col-span-2 bg-white rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800">积分趋势</h3>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-emerald-400 inline-block" />
                    消耗（兑换）
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-sky-400 inline-block" />
                    发放（收入）
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gray-200 inline-block" />
                    订单数
                  </span>
                </div>
              </div>
              <div className="flex items-end gap-1 h-44 overflow-x-auto pb-1">
                {dayStats.map((d) => (
                  <div key={d.date} className="flex flex-col items-center gap-1 flex-1 min-w-[32px] group">
                    <div className="relative w-full flex flex-col items-center gap-0.5">
                      {/* Points in bar */}
                      <div
                        className="w-full bg-sky-400 rounded-t-sm transition-all duration-500 group-hover:bg-sky-500"
                        style={{ height: `${Math.max((d.pointsIn / maxPointsIn) * 60, d.pointsIn > 0 ? 4 : 0)}px` }}
                        title={`发放 ${d.pointsIn.toLocaleString()} 积分`}
                      />
                      {/* Points out bar */}
                      <div
                        className="w-full bg-emerald-400 rounded-t-sm transition-all duration-500 group-hover:bg-emerald-500"
                        style={{ height: `${Math.max((d.points / maxPoints) * 60, d.points > 0 ? 4 : 0)}px` }}
                        title={`消耗 ${d.points.toLocaleString()} 积分`}
                      />
                      {/* Count bar */}
                      <div
                        className="w-full bg-gray-200 rounded-t-sm transition-all duration-500"
                        style={{ height: `${Math.max((d.orderCount / maxCount) * 24, d.orderCount > 0 ? 3 : 0)}px` }}
                        title={`订单 ${d.orderCount} 笔`}
                      />
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{d.date}</span>
                  </div>
                ))}
              </div>
              {orders.length === 0 && pointsLogs.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                  暂无数据
                </div>
              )}
            </div>

            {/* 订单状态分布 */}
            <div className="bg-white rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">订单状态分布</h3>
              {statusEntries.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">暂无数据</div>
              ) : (
                <div className="space-y-3">
                  {statusEntries.map(([status, count]) => (
                    <div key={status}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: STATUS_COLORS[status] ?? '#d1d5db' }}
                          />
                          <span className="text-sm text-gray-700">{status}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">{count}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(count / totalOrders) * 100}%`,
                            backgroundColor: STATUS_COLORS[status] ?? '#d1d5db',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 pt-1">共 {totalOrders} 笔订单</p>
                </div>
              )}
            </div>
          </div>

          {/* ── 商品排行 + 积分来源 + 时段分布 ── */}
          <div className="grid grid-cols-3 gap-4">
            {/* 商品销售排行 */}
            <div className="bg-white rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">商品兑换排行</h3>
              {topProducts.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">暂无数据</div>
              ) : (
                <div className="space-y-3">
                  {topProducts.map((p, idx) => (
                    <div key={p.name} className="flex items-center gap-3">
                      <span
                        className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded text-xs font-bold ${
                          idx === 0 ? 'bg-rose-500 text-white' :
                          idx === 1 ? 'bg-orange-400 text-white' :
                          idx === 2 ? 'bg-amber-400 text-white' :
                          'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700 truncate max-w-[140px]">{p.name}</span>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <span className="text-xs text-gray-400">×{p.quantity}</span>
                            <span className="text-sm font-semibold text-emerald-600">{p.points.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-400 rounded-full transition-all duration-700"
                            style={{ width: `${(p.points / maxProductPoints) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 积分来源分布 */}
            <div className="bg-white rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">积分来源分布</h3>
              {sourceEntries.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">暂无数据</div>
              ) : (
                <div className="space-y-3">
                  {sourceEntries.map(([source, points]) => (
                    <div key={source}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: SOURCE_COLORS[source] ?? '#d1d5db' }}
                          />
                          <span className="text-sm text-gray-700">{SOURCE_LABELS[source] ?? source}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">{points.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(points / totalSourcePoints) * 100}%`,
                            backgroundColor: SOURCE_COLORS[source] ?? '#d1d5db',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 pt-1">共 {totalSourcePoints.toLocaleString()} 积分</p>
                </div>
              )}
            </div>

            {/* 下单时段分布 */}
            <div className="bg-white rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">下单时段分布</h3>
              {orders.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">暂无数据</div>
              ) : (
                <>
                  <div className="flex items-end gap-0.5 h-28">
                    {hourDist.map((count, h) => (
                      <div key={h} className="flex-1 flex flex-col items-center gap-0.5 group">
                        <div
                          className="w-full bg-rose-300 rounded-t-sm group-hover:bg-rose-500 transition-colors cursor-default"
                          style={{ height: `${Math.max((count / maxHour) * 100, count > 0 ? 4 : 0)}px` }}
                          title={`${h}:00 — ${count} 笔`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-[9px] text-gray-400 mt-1 px-0.5">
                    <span>0时</span>
                    <span>6时</span>
                    <span>12时</span>
                    <span>18时</span>
                    <span>23时</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">高峰时段</span>
                      <span className="font-semibold text-rose-500">{peakHour}:00 — {peakHour + 1}:00</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">该时段下单 {hourDist[peakHour]} 笔</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminStatsPage;
